import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { AddJobState } from "../../model";
import { clearFields } from "../featureUtils";
import { getToken } from "../../utils/tokenStorage";
import { url } from "../../utils/config";
import { delayMessage } from "../../utils/delayMessage";
import { serverMessage } from "../../utils/serverMessage";
import { changeTextContent } from "../allJobs/editSlice";

const initialState: AddJobState = {
  addJobArr: [],
  isRemove: true, // to toggle side base
  jobLoading: false, // to show loading state
  jobDisable: false, // to activate and deactivate button
  inputs: {
    _id: "",
    position: "",
    company: "",
    jobLocation: "",
    jobDescription: "",
    jobStatus: "",
    jobType: "",
  },
  pendingRequestTimer: null,
};

// Create a job
export const submitData = createAsyncThunk(
  "addJob/submitData",
  async (jobData: AddJobState["inputs"], thunkAPI) => {
    // Destructure to exclude _id and use the rest operator to gather the rest of the properties
    const { _id, ...rest } = jobData;
    try {
      const token = getToken();
      const { data } = await axios.post(`${url.baseUrl}/job`, rest, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error: any) {
      console.log(error.response);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Update data
export const updateData = createAsyncThunk(
  "addJob/updateData",
  async (details: any, thunkAPI) => {
    const { inputs, id, dispatch } = details;
    try {
      const token = getToken();
      const { data } = await axios.patch(`${url.baseUrl}/job/${id}`, inputs, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(changeTextContent()); // Change text back to "Submit"

      return data;
    } catch (error: any) {
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const addJobSlice = createSlice({
  name: "addJob",
  initialState,
  reducers: {
    // Collect all inputs on the add job page
    collectInput: (state, action) => {
      const { name, value } = action.payload;
      state.inputs = {
        ...state.inputs,
        [name]: value,
      };
    },
    // Clear input fields when submit button is clicked
    clearInput: (state) => {
      clearFields(state); // This function is in a separate file that handles clearing of input fields
    },
    // Stage add job input fields when edit btn is clicked
    stageInput: (state, { payload }) => {
      state.inputs = {
        ...state.inputs,
        ...payload,
      };
    },
  },

  extraReducers: (builder) => {
    // Add Job functionality
    builder
      .addCase(submitData.pending, (state) => {
        state.jobLoading = true;
        state.jobDisable = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(submitData.fulfilled, (state, { payload }) => {
        clearFields(state);
        toast.success(payload.data.message);
        state.jobLoading = false;
        state.jobDisable = false;
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      })
      .addCase(submitData.rejected, (state, { payload }: any) => {
        toast.error(payload);
        state.jobLoading = false;
        state.jobDisable = false;
        serverMessage(payload); // Server error
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      });

    // Update functionality
    builder
      .addCase(updateData.pending, (state) => {
        state.jobLoading = true;
        state.jobDisable = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(updateData.fulfilled, (state, { payload }) => {
        clearFields(state);
        toast.success(payload.data.message); // Job update success message
        state.jobLoading = false;
        state.jobDisable = false;
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      })
      .addCase(updateData.rejected, (state, { payload }: any) => {
        state.jobLoading = false;
        state.jobDisable = false;
        serverMessage(payload); // Server error
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      });
  },
});

export const { collectInput, clearInput, stageInput } = addJobSlice.actions;

export default addJobSlice.reducer;
